import React, { useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  Switch,
  Text,
  TextLabel,
  useColorMode,
  useColorStyle,
  Checkbox,
} from '@tonic-one/react';
import { useSiderWidthStore } from '@Models/useSiderWidthModel';
import { PermissionNameEnum } from '@Types/permissions';
import { usePermissionStore } from '@Models/usePermissionModel';
import { useTranslation } from 'react-i18next';
import { handleNavigate } from '@Utils/common';
import { useNavigate, useParams } from 'react-router-dom';
import NavBack from '@Components/Common/NavBack';
import {
  InputChildRef,
  InputWithValid,
} from '@Components/Common/Form/InputWithValid';
import { ValidProps } from '@Types/index';
import {
  validateEmail,
  validateIsNotEmptyString,
  validateIsNotWhitespace,
} from '@Utils/validate';
import { AdministrationApi, GlobalApi } from '@Apis';
import { CreateAdmin } from '@Types/administrator';
import CSpinner from '@Components/CSpinner';
import { produce } from 'immer';
import { Info } from '@Components/Common/Info';
import AlertModal from '@Components/Common/Modal/AlertModal';
import { useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from 'src/QueryClient';
export function AddUser() {
  const { id: email } = useParams();
  const { t } = useTranslation();
  const [localAccounts, setLocalAccounts] = React.useState<CreateAdmin>({
    email: '',
    userDisplayname: '',
    roleId: '',
    ssoEnabled: 0,
    isInheritUser: false,
  });
  const { isPending: isGetSsoConfigPending, data: ssoConfigData } = useQuery({
    queryKey: ['getSsoConfig'],
    queryFn: async () => {
      return (await GlobalApi.getSsoConfig()).data;
    },
  });
  const { isPending, data: rolesData } = useQuery({
    queryKey: ['getRoles'],
    queryFn: async () => {
      return (await AdministrationApi.getRoles()).data;
    },
  });
  console.log('email', email);
  const {
    isPending: isGetLocalAccountsByEmailPending,
    data: currentUser,
    isError: isGetLocalAccountsByEmailError,
  } = useQuery({
    queryKey: ['getLocalAccountsByEmail', email],
    queryFn: async () => {
      if (!email) return null;
      const res = (await AdministrationApi.getLocalAccountsByEmail(email)).data;
      return res;
    },
  });

  if (isGetLocalAccountsByEmailError) {
    window.showNotification({
      titleMsg: t('common.apiException.internalError'),
      severity: 'error',
    });
  }
  useEffect(() => {
    console.log('currentUser', currentUser);
    if (currentUser) {
      setIsEdit(true);
      if (!ssoConfigData?.enableSamlSso) {
        setLocalAccounts({ ...currentUser, ssoEnabled: 0 });
      } else {
        setLocalAccounts(currentUser);
      }
    }
  }, [currentUser, ssoConfigData?.enableSamlSso]);
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { siderWidth } = useSiderWidthStore();
  const navigate = useNavigate();
  const [isEdit, setIsEdit] = React.useState(false);
  const { widthEditablePermission } = usePermissionStore();
  const [canBeInherited, setCanBeInherited] = React.useState<boolean>(false);
  const [alertMessage, setAlertMessage] = React.useState<{
    title: string;
    content: JSX.Element;
    open: boolean;
  }>({
    title: '',
    content: <></>,
    open: false,
  });
  const inputEmailRef = useRef<InputChildRef>(null);
  const inputNameRef = useRef<InputChildRef>(null);

  const goBack = function () {
    const path = `/administrator/user-management`;
    handleNavigate(path);
    navigate(path, { replace: true });
  };
  const checkAll = () => {
    inputEmailRef.current?.validate(localAccounts.email);
    if (inputEmailRef.current?.errorMsg !== undefined) return false;

    inputNameRef.current?.validate(localAccounts.userDisplayname);
    if (inputNameRef.current?.errorMsg !== undefined) return false;

    return true;
  };

  const checkIsMultiTenantUser = useMutation({
    mutationFn: async () => {
      return (await AdministrationApi.isMultiTenantUser(localAccounts.email))
        .data;
    },
    onSuccess: (isMultiTenantUserResult) => {
      setCanBeInherited(isMultiTenantUserResult.status);
    },
    onError: (error: any) => {
      setCanBeInherited(false);
      if (error?.data.error.details[0].message === 'Invalid email format.') {
        window.showNotification({
          titleMsg: t('common.error.invalidEmailAddress'),
          severity: 'error',
        });
      } else {
        window.showNotification({
          titleMsg: t('common.apiException.internalError'),
          severity: 'error',
        });
      }
    },
  });

  const createUserMutation = useMutation({
    mutationFn: async () => {
      if (!isEdit) {
        if (localAccounts.roleId === '') {
          await AdministrationApi.createLocalAccounts({
            ...localAccounts,
            roleId:
              rolesData?.find((role) => role.isDefault === true)?.roleId || '',
            language: localStorage.getItem('i18nextLng') || 'en',
          });
        } else {
          await AdministrationApi.createLocalAccounts({
            ...localAccounts,
            language: localStorage.getItem('i18nextLng') || 'en',
          });
        }
      } else {
        if (localAccounts.isInheritUser) {
          return;
        }
        const updateUserReq = {
          language: localStorage.getItem('i18nextLng') || 'en',
          ssoEnabled: localAccounts.ssoEnabled,
          roleId: localAccounts.roleId,
        };
        await AdministrationApi.updateLocalAccountsByEmail(
          localAccounts.email,
          updateUserReq
        );
      }
    },
    onSuccess: () => {
      if (!isEdit) {
        window.showNotification({
          titleMsg: t('administration.rbac.addUser.successMsg'),
          severity: 'success',
        });
      } else {
        window.showNotification({
          titleMsg: t('administration.rbac.updateUser.successMsg'),
          severity: 'success',
        });
      }
      queryClient
        .invalidateQueries({ queryKey: ['getLocalAccounts'] })
        .then(() => {
          navigate('/administrator/user-management');
        });
    },
    onError: (error: any) => {
      if (
        ['LoginAccount;ALREADY_EXISTS'].includes(
          error?.data.error.details[0].reason
        )
      ) {
        window.showNotification({
          titleMsg: t('administration.rbac.addUser.existMsg'),
          severity: 'error',
        });
        return;
      } else if (
        error?.data.error.details[0].reason ===
        'MULTI_TENANT_NOT_MY_LOCAL_ACCOUNT'
      ) {
        window.showNotification({
          titleMsg: t('administration.rbac.addUser.multiTenantExist'),
          severity: 'error',
        });
      } else if (
        error?.data.error.details[0].message === 'Invalid email format.'
      ) {
        window.showNotification({
          titleMsg: t('common.error.invalidEmailAddress'),
          severity: 'error',
        });
      } else {
        window.showNotification({
          titleMsg: t('common.apiException.internalError'),
          severity: 'error',
        });
      }
    },
  });

  const onSave = async () => {
    createUserMutation.mutate();
  };

  const handler = async () => {
    inputEmailRef.current?.validate(localAccounts.email);
    if (inputEmailRef.current?.errorMsg !== undefined) {
      return;
    }
    if (localAccounts.email) {
      checkIsMultiTenantUser.mutate();
    }
  };

  const validateEmailAddress: ValidProps[] = [
    {
      validMethod: validateEmail,
      errMsg: t('common.error.invalidEmailAddress'),
    },
    {
      validMethod: validateIsNotEmptyString,
      errMsg: t('reportCommon.tips.errors.requiredField'),
    },
  ];

  const validateName: ValidProps[] = [
    {
      validMethod: validateIsNotWhitespace,
      errMsg: t('reportCommon.tips.errors.requiredField'),
    },
  ];

  useEffect(() => {
    if (!isEdit && rolesData && !localAccounts.roleId) {
      const roleId = rolesData.find((d) => d.isDefault === true)?.roleId;
      if (roleId) {
        console.log({
          ...localAccounts,
          roleId,
        });
        setLocalAccounts({
          ...localAccounts,
          roleId,
        });
      }
    }
  }, [localAccounts, rolesData, isEdit]);

  return (
    <Box>
      {(isPending ||
        isGetLocalAccountsByEmailPending ||
        createUserMutation.isPending ||
        isGetSsoConfigPending) && <CSpinner />}
      <Box>
        <NavBack
          fixed={false}
          onBack={goBack}
          style={{ padding: '12px 16px 12px 16px' }}
        />
      </Box>
      <AlertModal
        visible={alertMessage.open}
        onClose={() => {
          setAlertMessage({
            ...alertMessage,
            open: false,
          });
        }}
        onConfirm={async () => {
          setAlertMessage({
            ...alertMessage,
            open: false,
          });
          await onSave();
        }}
        confirmButtonName={t('common.button.ok')}
        title={alertMessage?.title}
        content={alertMessage?.content}
      ></AlertModal>
      <Box id="addRoleSteps" p="0px 16px">
        <Flex flexDirection="column">
          <Box mb={12}>
            <Divider />
          </Box>
          <Box>
            <Text>{t('administration.rbac.addAdministrator.desc')}</Text>
          </Box>
          <Box mt="32px">
            <Grid templateColumns="min-content auto" columnGap="3x" rowGap="3x">
              <Grid>
                <TextLabel
                  whiteSpace="nowrap"
                  width="128px"
                  className="required"
                >
                  {t('globalSetting.highProfileUsers.column.emailAddress')}:
                </TextLabel>
              </Grid>
              <Grid>
                <InputWithValid
                  ref={inputEmailRef}
                  onBlur={handler}
                  onKeyDown={async (e: any) => {
                    if (e.key === 'Enter') {
                      await handler();
                    }
                  }}
                  onChange={(email: string) => {
                    setLocalAccounts(
                      produce(localAccounts, (draft) => {
                        draft.email = email;
                      })
                    );
                  }}
                  width="572px"
                  value={localAccounts.email}
                  validates={validateEmailAddress}
                  maxLength={255}
                  disabled={isEdit || localAccounts.isInheritUser}
                />
              </Grid>
              {canBeInherited && (
                <>
                  <Grid width="128px"></Grid>
                  <Grid>
                    <Flex alignItems="center" width="100%">
                      <Box mr={8}>
                        <Checkbox
                          disabled={
                            inputEmailRef.current?.errorMsg !== undefined ||
                            isEdit
                          }
                          checked={localAccounts.isInheritUser}
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            inputEmailRef.current?.validate(
                              localAccounts.email
                            );
                            if (inputEmailRef.current?.errorMsg !== undefined) {
                              return;
                            }

                            setLocalAccounts({
                              ...localAccounts,
                              isInheritUser: e.target.checked,
                            });
                          }}
                        >
                          {t('administration.rbac.multiTenant.desc')}
                        </Checkbox>
                      </Box>
                      <Info
                        content={t('administration.rbac.multiTenant.moreDesc')}
                      />
                    </Flex>
                  </Grid>
                </>
              )}
              <Grid>
                <TextLabel
                  whiteSpace="nowrap"
                  width="128px"
                  className="required"
                >
                  {t('common.label.enterName')}:
                </TextLabel>
              </Grid>
              <Grid>
                <InputWithValid
                  ref={inputNameRef}
                  onChange={(name: string) => {
                    setLocalAccounts(
                      produce(localAccounts, (draft) => {
                        draft.userDisplayname = name;
                      })
                    );
                  }}
                  width="572px"
                  value={localAccounts.userDisplayname}
                  validates={validateName}
                  maxLength={255}
                  disabled={isEdit}
                />
              </Grid>
              <Grid>
                <TextLabel
                  whiteSpace="nowrap"
                  width="128px"
                  className="required"
                >
                  {t('common.label.ssoToConsole')}:
                </TextLabel>
              </Grid>
              <Grid>
                {localAccounts.isInheritUser ? (
                  <Text>{t('administration.rbac.label.inherited')}</Text>
                ) : (
                  <Switch
                    checked={!!localAccounts.ssoEnabled}
                    disabled={!ssoConfigData?.enableSamlSso}
                    value={1}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setLocalAccounts({
                        ...localAccounts,
                        ssoEnabled: e.target.checked ? 1 : 0,
                      });
                    }}
                  />
                )}
              </Grid>

              <Grid>
                <TextLabel
                  whiteSpace="nowrap"
                  width="128px"
                  className="required"
                >
                  {t('common.label.role')}:
                </TextLabel>
              </Grid>

              <Grid mb={84}>
                {localAccounts.isInheritUser ? (
                  <Text>{t('administration.rbac.label.inherited')}</Text>
                ) : (
                  <>
                    {rolesData &&
                      rolesData.length > 0 &&
                      rolesData.map((role) => (
                        <Checkbox
                          mb={12}
                          key={role.roleId}
                          checked={role.roleId === localAccounts.roleId}
                          onChange={() =>
                            setLocalAccounts({
                              ...localAccounts,
                              roleId: role.roleId,
                            })
                          }
                        >
                          {role.isDefault === true
                            ? t('administration.rbac.label.defaultRole')
                            : role.roleName}
                        </Checkbox>
                      ))}
                    <Button
                      variant="secondary"
                      onClick={() => {
                        navigate(
                          '/administration/user-management/add-role-without-target'
                        );
                      }}
                      width={120}
                      disabled={
                        !widthEditablePermission(
                          PermissionNameEnum.administrator
                        )
                      }
                    >
                      {t('administration.rbac.label.addRole')}
                    </Button>
                  </>
                )}
              </Grid>
            </Grid>
          </Box>
        </Flex>
        <Flex
          position="fixed"
          pl="5x"
          bottom={0}
          left={270 + siderWidth}
          right={0}
          background={colorStyle.background.secondary}
          height={64}
          align="center"
          justify="flex-start"
        >
          <Button
            variant="primary"
            onClick={async () => {
              if (!checkAll()) return;
              if (!localAccounts.email || !localAccounts.userDisplayname) {
                return;
              }
              if (localAccounts.isInheritUser && !isEdit) {
                setAlertMessage({
                  title: '',
                  content: (
                    <Text>
                      {t('administration.rbac.multiTenant.alertMsg', {
                        email: localAccounts.email,
                      })}
                    </Text>
                  ),
                  open: true,
                });
                return;
              }
              await onSave();
            }}
            disabled={
              !widthEditablePermission(PermissionNameEnum.administrator)
            }
          >
            {t('common.button.save')}
          </Button>
        </Flex>
      </Box>
    </Box>
  );
}
